"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';

const wrongGoals = ['More followers.', 'More likes.', 'More views.'];

/* Featured reel for the right column */
const featuredReel = {
    videoSrc: '/videos/lw-reel-07.mp4',
    poster: '/images/our-work/instagram/lw-post-07.webp',
    handle: '@lead_workz',
    likes: '2.4K',
    comments: '186',
    label: 'Reel',
};

export default function SocialHero() {
    return (
        <section
            className="relative overflow-hidden min-h-screen flex flex-col justify-center"
            style={{ background: '#080400' }}
        >
            {/* Dot grid */}
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, rgba(206,255,26,0.05) 1.5px, transparent 1.5px)',
                backgroundSize: '36px 36px',
                maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 30%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 30%, transparent 100%)',
            }} />

            {/* Glow blobs */}
            <div className="absolute pointer-events-none" style={{ width: 800, height: 800, top: '-25%', left: '-20%', background: 'radial-gradient(circle, rgba(55,48,163,0.09) 0%, transparent 65%)', borderRadius: '50%' }} />
            <div className="absolute pointer-events-none" style={{ width: 500, height: 500, bottom: '-10%', right: '-8%', background: 'radial-gradient(circle, rgba(206,255,26,0.05) 0%, transparent 65%)', borderRadius: '50%' }} />

            <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-16 lg:px-24 pt-36 pb-20 z-10">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-center">

                    {/* ── LEFT COLUMN ── */}
                    <div className="flex flex-col gap-10 max-w-[700px]">

                        {/* Eyebrow */}
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-center gap-3"
                        >
                            <span style={{ display: 'block', width: 28, height: 2, background: '#ceff1a', borderRadius: 2, flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                                Social Media Marketing — Bangalore
                            </span>
                        </motion.div>

                        {/* Main Headline */}
                        <motion.h1
                            initial={{ opacity: 0, y: 32 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                            style={{
                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                fontWeight: 800,
                                fontSize: 'clamp(38px, 5vw, 76px)',
                                lineHeight: 1.05,
                                letterSpacing: '-0.025em',
                                color: '#fbfdfd',
                                margin: 0,
                            }}
                        >
                            Social Media<br />
                            Doesn&apos;t Just<br />
                            Build{' '}
                            <span style={{ color: '#ceff1a', position: 'relative', display: 'inline-block' }}>
                                Reach.
                                <motion.span
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: 0.6, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ position: 'absolute', bottom: 4, left: 0, right: 0, height: 3, background: '#ceff1a', borderRadius: 2, transformOrigin: 'left', display: 'block' }}
                                />
                            </span>
                            <br />
                            It Builds{' '}
                            <span style={{ background: 'linear-gradient(135deg, #ceff1a 0%, #a8d400 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                Trust.
                            </span>
                        </motion.h1>

                        {/* Wrong goals block */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            style={{ borderLeft: '2px solid rgba(206,255,26,0.18)', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}
                        >
                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 14, color: 'rgba(251,253,253,0.38)', letterSpacing: '0.02em', margin: '0 0 10px' }}>
                                Most businesses approach social media with the wrong goal.
                            </p>
                            {wrongGoals.map((g, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.45 + i * 0.1 }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                                >
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.35 }}>
                                        <path d="M2 2l8 8M10 2l-8 8" stroke="#fbfdfd" strokeWidth="1.6" strokeLinecap="round"/>
                                    </svg>
                                    <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 15, fontWeight: 600, color: 'rgba(251,253,253,0.28)', textDecoration: 'line-through', textDecorationColor: 'rgba(251,253,253,0.15)', textDecorationThickness: '1.5px', letterSpacing: '0.01em' }}>
                                        {g}
                                    </span>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Divider */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.75 }}
                            style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                        >
                            <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, rgba(206,255,26,0.25) 0%, transparent 100%)' }} />
                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(206,255,26,0.45)', whiteSpace: 'nowrap' }}>
                                But visibility alone doesn&apos;t create growth
                            </span>
                            <div style={{ height: 1, flex: 1, background: 'linear-gradient(90deg, transparent 0%, rgba(206,255,26,0.25) 100%)' }} />
                        </motion.div>

                        {/* Body content */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.85 }}
                            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                        >
                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 15, lineHeight: 1.75, color: 'rgba(251,253,253,0.52)', letterSpacing: '0.02em', margin: 0 }}>
                                People buy from brands they{' '}
                                <span style={{ color: 'rgba(251,253,253,0.80)', fontWeight: 600 }}>recognise, trust, and remember</span>.
                                {' '}Social media plays a critical role in shaping that perception long before a customer fills out a form, visits a website, or makes a purchase.
                            </p>

                            <div style={{ background: 'rgba(206,255,26,0.04)', border: '1px solid rgba(206,255,26,0.12)', borderRadius: 14, padding: '16px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                <div style={{ width: 3, minHeight: 48, background: 'linear-gradient(180deg, #ceff1a 0%, rgba(206,255,26,0.1) 100%)', borderRadius: 2, flexShrink: 0, marginTop: 3 }} />
                                <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 14, lineHeight: 1.75, color: 'rgba(251,253,253,0.65)', margin: 0 }}>
                                    At <span style={{ color: '#ceff1a', fontWeight: 700 }}>Lead Workz</span>, we help businesses use social media strategically — not as a content machine, but as a{' '}
                                    <span style={{ color: 'rgba(251,253,253,0.90)', fontWeight: 600 }}>trust-building system</span>.
                                </p>
                            </div>

                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 14, lineHeight: 1.75, color: 'rgba(251,253,253,0.40)', margin: 0, letterSpacing: '0.01em' }}>
                                By combining consumer psychology, content strategy, creative storytelling, and platform expertise, we create social media ecosystems that strengthen brand perception and support business growth.
                            </p>

                            <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 16, fontWeight: 700, color: 'rgba(251,253,253,0.70)', letterSpacing: '0.01em', margin: 0, fontStyle: 'italic' }}>
                                Because before people buy from you, they need a reason to{' '}
                                <span style={{ color: '#ceff1a' }}>believe in you.</span>
                            </p>
                        </motion.div>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.0 }}
                            className="flex flex-col sm:flex-row gap-4"
                        >
                            <Link
                                href="/contact"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_0_32px_rgba(206,255,26,0.3)]"
                                style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 15, letterSpacing: '0.06em' }}
                            >
                                Book A Strategy Session
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="#080400" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </Link>
                            <Link
                                href="/contact"
                                className="inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:bg-white/5"
                                style={{ border: '1.5px solid rgba(251,253,253,0.15)', color: 'rgba(251,253,253,0.75)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 15, letterSpacing: '0.06em' }}
                            >
                                Talk to a Strategist
                            </Link>
                        </motion.div>
                    </div>

                    {/* ── RIGHT COLUMN — single featured reel ── */}
                    <div className="flex flex-col items-center justify-center relative" style={{ height: 700 }}>

                        {/* Floating card wrapper */}
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.93 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            style={{ position: 'relative', width: '100%', maxWidth: 300, margin: '0 auto' }}
                        >
                            <div>
                                {/* Glow behind the card */}
                                <div style={{
                                    position: 'absolute', inset: -24, borderRadius: 32,
                                    background: 'radial-gradient(ellipse at 50% 60%, rgba(206,255,26,0.12) 0%, transparent 70%)',
                                    filter: 'blur(16px)',
                                    pointerEvents: 'none',
                                }} />

                                {/* Phone-style card */}
                                <div style={{
                                    background: '#0e0e0e',
                                    borderRadius: 28,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    boxShadow: '0 32px 80px rgba(0,0,0,0.75), 0 0 0 1px rgba(206,255,26,0.07)',
                                    position: 'relative',
                                }}>

                                    {/* Instagram header */}
                                    <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, background: '#111' }}>
                                        {/* Lead Workz logo avatar */}
                                        <div style={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            border: '2px solid rgba(206,255,26,0.4)',
                                            flexShrink: 0,
                                            overflow: 'hidden',
                                            background: '#080400',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <Image
                                                src="/images/shared/logo_favi_color.webp"
                                                alt="Lead Workz"
                                                width={32}
                                                height={32}
                                                style={{ objectFit: 'contain', width: '100%', height: '100%', filter: 'brightness(0) invert(1)' }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 12, fontWeight: 700, color: '#fbfdfd', letterSpacing: '0.04em', margin: 0 }}>
                                                {featuredReel.handle}
                                            </p>
                                            <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 9, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
                                                Reel · Lead Workz
                                            </p>
                                        </div>
                                        {/* Verified */}
                                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(206,255,26,0.15)', border: '1px solid rgba(206,255,26,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                                                <path d="M2 5l2.5 2.5L8 3" stroke="#ceff1a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        {/* Three dots */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 4px' }}>
                                            {[0,1,2].map(i => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />)}
                                        </div>
                                    </div>

                                    {/* Reel video — 9:16 portrait */}
                                    <div style={{ position: 'relative', width: '100%', aspectRatio: '9/16', overflow: 'hidden', background: '#000' }}>
                                        <video
                                            src={featuredReel.videoSrc}
                                            poster={featuredReel.poster}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                        {/* Bottom gradient */}
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)', pointerEvents: 'none' }} />

                                        {/* Reel label bottom-left */}
                                        <div style={{ position: 'absolute', bottom: 12, left: 14, pointerEvents: 'none' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)', borderRadius: 6, padding: '4px 9px', border: '1px solid rgba(255,255,255,0.12)' }}>
                                                {/* Reel icon */}
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
                                                    <path d="M19.82 2H4.18A2.18 2.18 0 002 4.18v15.64A2.18 2.18 0 004.18 22h15.64A2.18 2.18 0 0022 19.82V4.18A2.18 2.18 0 0019.82 2zM7 2l4 6H7V2zm6 0l4 6h-4V2zm5 6l-4-6h2.82A.18.18 0 0119 2.18V8h-1zM2 9h20v11a.18.18 0 01-.18.18H4.18A.18.18 0 014 20V9H2z"/>
                                                </svg>
                                                <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 10, fontWeight: 700, color: 'white', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                                                    Reel
                                                </span>
                                            </div>
                                        </div>

                                        {/* Live / playing indicator top-right */}
                                        <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', alignItems: 'center', gap: 5, pointerEvents: 'none' }}>
                                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ceff1a', boxShadow: '0 0 6px rgba(206,255,26,0.9)' }} />
                                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                                                Playing
                                            </span>
                                        </div>
                                    </div>

                                    {/* Engagement footer */}
                                    <div style={{ padding: '12px 16px', background: '#111', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {/* Action icons */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 21C12 21 3 14 3 8a5 5 0 019-3 5 5 0 019 3c0 6-9 13-9 13z" stroke="white" strokeWidth="1.8"/></svg>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="white" strokeWidth="1.8" strokeLinejoin="round"/></svg>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2L15 22l-4-9-9-4 19-7z" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            <div style={{ marginLeft: 'auto' }}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" stroke="white" strokeWidth="1.8" strokeLinejoin="round"/></svg>
                                            </div>
                                        </div>
                                        {/* Likes */}
                                        <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 12, fontWeight: 700, color: '#fbfdfd', margin: 0, letterSpacing: '0.04em' }}>
                                            {featuredReel.likes} likes
                                        </p>
                                        {/* Caption preview */}
                                        <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 11, color: 'rgba(255,255,255,0.40)', margin: 0, lineHeight: 1.5 }}>
                                            <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>lead_workz </span>
                                            Ready to stop blending in? ✨
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Floating engagement badge — right of card */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.1, duration: 0.5 }}
                            className="hidden lg:flex"
                            style={{
                                position: 'absolute',
                                top: '18%',
                                left: -80,
                                background: 'rgba(8,4,0,0.92)',
                                border: '1px solid rgba(206,255,26,0.25)',
                                borderRadius: 14,
                                padding: '10px 14px',
                                backdropFilter: 'blur(16px)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                                zIndex: 20,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="#ceff1a">
                                <path d="M12 21C12 21 3 14 3 8a5 5 0 019-3 5 5 0 019 3c0 6-9 13-9 13z"/>
                            </svg>
                            <div>
                                <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 13, fontWeight: 800, color: '#ceff1a', margin: 0, letterSpacing: '-0.01em' }}>↑ 127%</p>
                                <p style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontSize: 9, color: 'rgba(255,255,255,0.35)', margin: 0 }}>Engagement</p>
                            </div>
                        </motion.div>

                    </div>

                </div>
            </div>
        </section>
    );
}

